import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Menu, X } from 'lucide-react'
import { CONTACT_INFO } from '@/lib/index'
import { springPresets } from '@/lib/motion'
import { t, Lang } from '@/lib/translations'

interface LayoutProps {
  children: React.ReactNode
  lang: Lang
  setLang: (l: Lang) => void
}

export function Layout({ children, lang, setLang }: LayoutProps) {
  const [activeSection, setActiveSection] = useState('hero')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const T = t[lang]
  const isAr = lang === 'ar'
  const dir = isAr ? 'rtl' : 'ltr'

  const NAV_ITEMS = [
    { id: 'about',          label: T.nav.about,          href: '#about' },
    { id: 'services',       label: T.nav.services,       href: '#services' },
    { id: 'projects',       label: T.nav.projects,       href: '#projects' },
    { id: 'fleet',          label: T.nav.fleet,          href: '#fleet' },
    { id: 'certifications', label: T.nav.certifications, href: '#certifications' },
    { id: 'contact',        label: T.nav.contact,        href: '#contact' },
  ]

  useEffect(() => {
    document.documentElement.dir = dir
    document.documentElement.lang = lang
    document.documentElement.style.fontFamily = isAr
      ? "'Cairo', 'Noto Sans Arabic', sans-serif"
      : "'Open Sans', sans-serif"
  }, [lang, dir, isAr])

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['hero', 'about', 'services', 'projects', 'fleet', 'certifications', 'contact']
      const scrollPosition = window.scrollY + 100
      for (const section of sections) {
        const element = document.getElementById(section)
        if (element) {
          const { offsetTop, offsetHeight } = element
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section)
            break
          }
        }
      }
    }
    window.addEventListener('scroll', handleScroll)
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleNavClick = (href: string) => {
    setMobileMenuOpen(false)
    const element = document.querySelector(href)
    if (element) element.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <div className="min-h-screen bg-background text-foreground" dir={dir}>
      <header className="fixed top-0 left-0 right-0 z-50 h-28 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="container mx-auto px-4 h-full">
          <div className="flex items-center justify-between h-full">
            {/* Logo */}
            <a
              href="#hero"
              onClick={(e) => { e.preventDefault(); handleNavClick('#hero') }}
              className="flex items-center gap-2 group"
            >
              <img
                src="/images/Screenshot 2026-05-07 083845.png"
                alt="RAC Logo"
                className="h-32 w-32 object-contain rounded-full group-hover:scale-105 transition-transform drop-shadow-md"
              />
              <div className="hidden sm:block">
                <div className="text-lg font-bold text-primary tracking-wide">
                  {isAr ? 'ربوع آلصمان' : 'RBOUA ALSMAN'}
                </div>
                <div className="text-sm text-muted-foreground">
                  {isAr ? 'للمقاولات' : 'CONTRACTING EST.'}
                </div>
              </div>
            </a>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-2">
              {NAV_ITEMS.map((item) => (
                <a
                  key={item.id}
                  href={item.href}
                  onClick={(e) => { e.preventDefault(); handleNavClick(item.href) }}
                  className={`flex items-center px-5 py-1.5 border text-sm font-bold transition-all duration-200 ${
                    activeSection === item.id
                      ? 'bg-primary text-white border-primary'
                      : 'border-primary/40 text-primary hover:bg-primary hover:text-white'
                  }`}
                  style={{ clipPath: 'polygon(8% 0%, 100% 0%, 92% 100%, 0% 100%)' }}
                >
                  {item.label}
                </a>
              ))}
            </nav>

            {/* Language toggle + mobile burger */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setLang(isAr ? 'en' : 'ar')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-primary/40 text-xs font-bold text-primary hover:bg-primary hover:text-white transition-all duration-200"
                aria-label="Toggle language"
              >
                <span className="text-base leading-none">{isAr ? '🇺🇸' : '🇸🇦'}</span>
                <span>{isAr ? 'EN' : 'عربي'}</span>
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 hover:bg-muted rounded-md transition-colors"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={springPresets.gentle}
            className="lg:hidden border-t border-border bg-card"
          >
            <nav className="container mx-auto px-4 py-4 flex flex-col gap-2">
              {NAV_ITEMS.map((item) => (
                <a
                  key={item.id}
                  href={item.href}
                  onClick={(e) => { e.preventDefault(); handleNavClick(item.href) }}
                  className={`px-4 py-3 rounded-md text-sm font-semibold transition-colors ${
                    activeSection === item.id
                      ? 'bg-primary/10 text-primary border border-primary/20'
                      : 'hover:bg-muted text-foreground'
                  }`}
                >
                  {item.label}
                </a>
              ))}
            </nav>
          </motion.div>
        )}
      </header>

      <main className="pt-28">{children}</main>

      {/* Footer */}
      <footer className="bg-card border-t border-border mt-24" dir={dir}>
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="/images/Screenshot 2026-05-07 083845.png"
                  alt="RAC Logo"
                  className="h-32 w-32 object-contain rounded-full drop-shadow-md"
                />
                <div>
                  <div className="text-base font-bold text-primary tracking-wide">
                    {isAr ? 'ربوع آلصمان' : 'RBOUA ALSMAN'}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {isAr ? 'للمقاولات' : 'CONTRACTING EST.'}
                  </div>
                </div>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{CONTACT_INFO.arabicName}</p>
              <p className="text-sm text-muted-foreground mt-2">{T.footer.tagline}</p>
            </div>

            <div>
              <h3 className="text-sm font-bold text-foreground mb-4 uppercase tracking-wide">{T.footer.contact}</h3>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>{CONTACT_INFO.address}</p>
                <p>{CONTACT_INFO.poBox}</p>
                <p>{isAr ? 'هاتف: ' : 'Phone: '}{CONTACT_INFO.phone}</p>
                <p>{isAr ? 'بريد: ' : 'Email: '}{CONTACT_INFO.email}</p>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-bold text-foreground mb-4 uppercase tracking-wide">{T.footer.quickLinks}</h3>
              <div className="space-y-2">
                {NAV_ITEMS.map((item) => (
                  <a
                    key={item.id}
                    href={item.href}
                    onClick={(e) => { e.preventDefault(); handleNavClick(item.href) }}
                    className="block text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-border text-center">
            <p className="text-sm text-muted-foreground">
              © 2026 {CONTACT_INFO.companyName}. {T.footer.rights}
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              ISO 9001:2015 | ISO 14001:2015 | ISO 45001:2018 | SACS-002 Certified
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}
